import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Head } from "vite-react-ssg";
import wmUrl from "../../../assets/brand/n-green.svg";
import type { Lang } from "../../../shared/i18n/lang";
import { localizedPath } from "../../../shared/i18n/lang";
import { GOALS, reachGoal } from "../../../shared/lib/metrika";
import { usePublicSettings } from "../../../shared/lib/usePublicSettings";
import { Btn, Icon } from "../../../shared/ui";
import PrivacyFooter from "../../../widgets/footer/PrivacyFooter";
import PrivacyHeader from "../../../widgets/header/PrivacyHeader";
import "./not-found.css";

// Catch-all page. It is prerendered once per language (/404 → 404.html, /ru/404 → ru/404.html)
// and nginx serves that file, with a 404 status, for every unknown path. Not in ROUTE_PATHS:
// no canonical, no hreflang, no sitemap entry — only noindex.
const NotFoundPage = () => {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === "ru" ? "ru" : "uz";
  const settings = usePublicSettings();

  // The HTML was rendered for /404, but the visitor is on some other path, and the language
  // switches build their links from the current path. React keeps the server's attributes when
  // it hydrates, so remount once on the client to give those links the real path.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return (
    <div className="lp nf-page" key={mounted ? "client" : "ssg"}>
      <Head>
        <html lang={lang} />
        <title>{t("not_found.meta_title")}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <PrivacyHeader />
      <main id="main" className="nf">
        <img className="wm" src={wmUrl} alt="" />
        <div className="wrap nf-in">
          <span className="nf-code" aria-hidden="true">
            404
          </span>
          <h1 className="h1">
            {t("not_found.title_pre")}
            <em>{t("not_found.title_accent")}</em>
          </h1>
          <p className="lead">{t("not_found.text")}</p>
          <div className="nf-btns">
            <Btn as="a" variant="primary" float href={localizedPath("/", lang)}>
              {t("not_found.home_btn")}
            </Btn>
            <Btn
              as="a"
              variant="secondary"
              href={settings.supportTelegramLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => reachGoal(GOALS.telegram)}
            >
              <Icon name="g-tg" className="glyph" />
              {t("cta.telegram_btn")}
            </Btn>
          </div>
        </div>
      </main>
      <PrivacyFooter />
    </div>
  );
};

export default NotFoundPage;
